/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryLowStockPage.tsx
 * ============================================================
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { usePantryContext } from './PantryLayout';
import { useLowStock } from '@/hooks/usePantry';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import PantryRow from './PantryRow';

export default function PantryLowStockPage() {
  const { homeId } = usePantryContext();
  const { data, isLoading } = useLowStock(homeId);
  const items = data?.items ?? [];

  if (isLoading) return <Skeleton className="h-48" />;

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500/60 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nothing low right now. 🎉</p>
          <p className="text-[11px] text-muted-foreground mt-1">
            Items appear here when quantity drops to zero, or at or below their min-stock level.
          </p>
        </CardContent>
      </Card>
    );
  }

  const outOfStock = items.filter((i) => i.isOutOfStock);
  const lowStock = items.filter((i) => !i.isOutOfStock);

  return (
    <div className="space-y-4 pb-16">
      {outOfStock.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-rose-600 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Out of stock ({outOfStock.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {outOfStock.map((it) => <PantryRow key={it.id} item={it} homeId={homeId} />)}
            </CardContent>
          </Card>
        </section>
      )}
      {lowStock.length > 0 && (
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Running low ({lowStock.length})
          </h3>
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {lowStock.map((it) => <PantryRow key={it.id} item={it} homeId={homeId} />)}
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
